import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams } from 'react-router-dom';
import { RootState } from '../store/store';
import { CheckCircleIcon, ClipboardDocumentIcon, LockClosedIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../components_auth/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Heading, Subheading } from '../components_catalyst/heading';
import { Text, TextLink, Code } from '../components_catalyst/text';
import { Badge } from '../components_catalyst/badge';
import { Button } from '../components_catalyst/button';

import { MCP_ACCESS_REQUEST_EMAIL } from './mcpAccess';

export { MCP_ACCESS_REQUEST_EMAIL };
const SERVER_NAME = 'milestone-maps';

type AccessState = 'loading' | 'allowed' | 'not-allowed';

/** Canonical MCP URL for this environment (REACT_APP_MCP_URL), or '' when unset. */
export function getMcpUrl(): string {
  return (process.env.REACT_APP_MCP_URL || '').trim();
}

/**
 * "Connect an AI agent" page (/:userName/settings/agents).
 * Allowlist state comes from the `mcpAccess` custom claim on the Firebase ID token
 * (not the profile field). See docs/roadmap/add-mcp/SPEC.Architecture.md section 5.2.
 */
export function ConnectAgentPage() {
  useDocumentTitle('Connect an AI agent');
  const { user } = useAuth();
  const { userName } = useParams<{ userName: string }>();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const [access, setAccess] = useState<AccessState>('loading');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mcpUrl = getMcpUrl();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setAccess('loading');
    user
      .getIdTokenResult(true)
      .then((result) => {
        if (!cancelled) setAccess(result.claims?.mcpAccess === true ? 'allowed' : 'not-allowed');
      })
      .catch(() => {
        if (!cancelled) setAccess('not-allowed');
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );

  const handleCopy = async () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    copyTimer.current = setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const mailto = `mailto:${MCP_ACCESS_REQUEST_EMAIL}?subject=${encodeURIComponent(
    'MCP ACCESS REQUEST'
  )}&body=${encodeURIComponent(`Please enable AI agent (MCP) access for ${user?.email ?? 'my account'}.`)}`;

  if (!userProfile || !userProfile.userName) {
    return (
      <div className="max-w-2xl mx-auto p-6" role="status" aria-live="polite">
        <Text>Loading...</Text>
      </div>
    );
  }
  if (userName !== userProfile.userName) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-8">
      <div>
        <Heading level={1}>Connect an AI agent</Heading>
        <Text className="mt-2">
          Let an AI assistant such as Claude read your milestone maps through the Model Context Protocol (MCP).
        </Text>
      </div>

      <section aria-labelledby="mcp-access-heading" className="space-y-3">
        <Subheading level={2} id="mcp-access-heading">
          Access
        </Subheading>
        <div role="status" aria-live="polite" data-testid="mcp-access-state">
          {access === 'loading' && <Text>Checking your access...</Text>}
          {access === 'allowed' && (
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="size-5 text-green-600" aria-hidden="true" />
              <Badge color="green">Enabled</Badge>
              <Text>AI agent access is enabled for your account.</Text>
            </div>
          )}
          {access === 'not-allowed' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <LockClosedIcon className="size-5 text-zinc-500" aria-hidden="true" />
                <Badge color="zinc">Not enabled</Badge>
                <Text>AI agent access is not enabled for your account yet.</Text>
              </div>
              <Text>
                <TextLink href={mailto} data-testid="mcp-request-access">
                  Request access
                </TextLink>{' '}
                and we will let you know when it is on.
              </Text>
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby="mcp-url-heading" className="space-y-3">
        <Subheading level={2} id="mcp-url-heading">
          Server URL
        </Subheading>
        {mcpUrl ? (
          <div className="flex items-center gap-2">
            <Code data-testid="mcp-url">{mcpUrl}</Code>
            <Button outline onClick={handleCopy}>
              <ClipboardDocumentIcon data-slot="icon" aria-hidden="true" />
              {copyStatus === 'copied' ? 'Copied' : 'Copy URL'}
            </Button>
            <span role="status" aria-live="polite" className="sr-only" data-testid="mcp-copy-status">
              {copyStatus === 'copied' ? 'Server URL copied' : copyStatus === 'failed' ? 'Copy failed' : ''}
            </span>
            {copyStatus === 'failed' && (
              <Text className="text-red-600 dark:text-red-400" data-testid="mcp-copy-failed">
                Copy failed. Select the URL and copy it manually.
              </Text>
            )}
          </div>
        ) : (
          <Text data-testid="mcp-url-missing">
            The MCP server URL is not configured for this environment.
          </Text>
        )}
      </section>

      <section aria-labelledby="mcp-instructions-heading" className="space-y-4">
        <Subheading level={2} id="mcp-instructions-heading">
          Setup instructions
        </Subheading>
        <div>
          <Subheading level={3}>
            claude.ai and Claude Desktop
          </Subheading>
          <ol className="list-decimal pl-6 mt-2 space-y-1 text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
            <li>Open Settings, then Connectors.</li>
            <li>Choose &quot;Add custom connector&quot;.</li>
            <li>Paste the server URL above and save.</li>
            <li>Sign in to Milestone Map when prompted and approve access.</li>
          </ol>
        </div>
        <div>
          <Subheading level={3}>
            Claude Code
          </Subheading>
          <pre className="mt-2 overflow-x-auto rounded-md bg-zinc-100 p-3 text-sm dark:bg-zinc-800">
            <code data-testid="mcp-claude-code-command">{`claude mcp add --transport http ${SERVER_NAME} ${mcpUrl || '<server-url>'}`}</code>
          </pre>
        </div>
        <div>
          <Subheading level={3}>
            Other MCP clients
          </Subheading>
          <Text className="mt-2">
            Use any client that supports remote (streamable HTTP) MCP servers with OAuth. Point it at the server URL
            above; it will discover the sign-in flow automatically.
          </Text>
        </div>
      </section>

      {/* SLOT: grants section (list/revoke connected agents) is added by mr-d5js.48. */}
      <section aria-label="Connected agents" data-testid="mcp-grants-slot" hidden />
    </div>
  );
}
